<template>
  <div
    class="link-item"
    v-if="!linkIsHidden"
    @click="handleClick"
    :title="linkLabel"
    :class="{
      'is-external': isExternalLink,
      'is-disabled': disabled,
    }"
  >
    <div class="link-dot"></div>
    <div class="link-content">
      <div class="link-title-container">
        <span class="link-title">{{ linkLabel }}</span>
      </div>
      <IconRenderer
        v-if="isExternalLink"
        name="open-outline"
        size="verySmall"
        class="external-icon"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/common/stores/userStore';
import { NavigationService } from '@/features/menu-management/utils/navigationUtils';
import type { MenuLinkModel } from '@/features/menu-management/types/menuTypes';
import { LinkTypeEnum } from '@/features/menu-management/constants/menuConstants';

import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

interface Props {
  link: MenuLinkModel;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

const emit = defineEmits<{
  click: [link: MenuLinkModel];
}>();

// Vue Router instance
const router = useRouter();
const userStore = useUserStore();

const linkIsHidden = computed(() => props.link.hidden);

// Get current locale
const currentLocale = computed(() => userStore.culture || 'fr');

// Computed property for the localized link label
const linkLabel = computed(() => {
  const locale = currentLocale.value;

  // Try to find translation for current locale
  if (props.link.nameTranslations && props.link.nameTranslations.length > 0) {
    const translation = props.link.nameTranslations.find((trans) =>
      trans.culture.toLowerCase().startsWith(locale.toLowerCase())
    );
    if (translation) {
      return translation.value;
    }
  }

  // Fallback to default name
  return props.link.name;
});

// Check if link is external
const isExternalLink = computed(() => {
  return props.link.linkType === LinkTypeEnum.External;
});

// Handle link click
function handleClick() {
  if (props.disabled) return;

  emit('click', props.link);

  const getUrlCurrentLocale = () => {
    if (props.link.urlTranslations && props.link.urlTranslations.length > 0) {
      const translation = props.link.urlTranslations.find((trans) =>
        trans.culture.startsWith(currentLocale.value)
      );
      if (translation) {
        return translation.value;
      }
    }
    return props.link.url;
  };

  const navigationLink = {
    url: getUrlCurrentLocale(),
    type: props.link.linkType,
  };

  NavigationService.handleLinkNavigation(navigationLink, router);
}
</script>

<style scoped>
.link-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.link-item:hover {
  background: rgba(var(--ion-color-primary-rgb), 0.08);
}

.link-item:hover.is-external {
  background: rgba(var(--ion-color-secondary-rgb), 0.08);
}

.link-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.link-item.is-disabled:hover {
  background: transparent;
}

.link-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ion-color-medium);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.link-item:hover .link-dot {
  background: var(--ion-color-primary);
  transform: scale(1.2);
}

.link-item.is-external:hover .link-dot {
  background: var(--ion-color-secondary);
}

.link-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

.link-title-container {
  flex: 1;
  min-width: 0;
}

.link-title {
  font-size: 13px;
  color: var(--ion-text-color);
  line-height: 1.3;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.link-item:hover .link-title {
  color: var(--ion-color-primary);
}

.link-item.is-external:hover .link-title {
  color: var(--ion-color-secondary);
}

.external-icon {
  color: var(--ion-color-medium);
  flex-shrink: 0;
  margin-left: 8px;
  opacity: 0.7;
}

.link-item:hover .external-icon {
  color: var(--ion-color-secondary);
  opacity: 1;
}

.link-item.is-external::after {
  content: '';
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
